import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints, US_STATES } from '../../consts'
import logger from '../../utils/logger'
import MapboxAddressAutocomplete from './MapboxAddressAutocomplete'
import styles from './PaymentMethodSelector.module.scss'

const ZIP_REGEX = /^\d{5}(-\d{4})?$/
const ROUTING_LOOKUP_DEBOUNCE_MS = 300

// ABA X9 checksum (mirror of api/services/billing/bankDetailsService.js).
function isValidAbaChecksum(routingNumber) {
  if (!/^\d{9}$/.test(routingNumber)) return false
  const d = routingNumber.split('').map(Number)
  const sum = 3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8])
  return sum % 10 === 0
}

const ACH_AUTH =
  'I authorize Veta Virtual to debit my account for recurring monthly charges and any applicable additional invoices, in accordance with NACHA rules. I can revoke this authorization at any time.'

// Empty initial state for the ACH form.
const emptyAch = () => ({
  account_holder_name: '',
  account_type: '',
  routing_number: '',
  routing_number_confirm: '',
  account_number: '',
  account_number_confirm: '',
  bank_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
})

const InlineBankForm = ({ paymentType, billingCustomerId, onSaved, existingDetails }) => {
  const [fields, setFields] = useState(() => {
    const seed = emptyAch()
    if (existingDetails) {
      Object.keys(seed).forEach((k) => {
        if (existingDetails[k] !== undefined && existingDetails[k] !== null)
          seed[k] = existingDetails[k]
      })
    }
    return seed
  })
  const [errors, setErrors] = useState({})
  const [authorized, setAuthorized] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Routing-lookup state
  const [bankResolved, setBankResolved] = useState(false)
  const [bankLookupError, setBankLookupError] = useState(null)
  const [bankLookupLoading, setBankLookupLoading] = useState(false)
  const lookupSeqRef = useRef(0)

  // When the API can't resolve the routing number, the user must re-type the
  // routing number into a confirm field instead of supplying a bank name.
  // This proves the number is intentional (no fat-finger) without forcing the
  // user to identify their bank — bank_name stays null for these saves.
  const routingConfirmed =
    !bankResolved &&
    /^\d{9}$/.test(fields.routing_number) &&
    isValidAbaChecksum(fields.routing_number) &&
    fields.routing_number === fields.routing_number_confirm

  // Mapbox state
  const [mapboxPlaceId, setMapboxPlaceId] = useState(existingDetails?.mapbox_place_id || null)
  const [addressVerifiedByMapbox, setAddressVerifiedByMapbox] = useState(
    Boolean(existingDetails?.address_verified_by_mapbox),
  )

  // Debounced routing-number lookup
  useEffect(() => {
    const rn = fields.routing_number
    setBankLookupError(null)

    if (!rn || rn.length !== 9) {
      setBankResolved(false)
      return undefined
    }
    if (!isValidAbaChecksum(rn)) {
      setBankResolved(false)
      setBankLookupError('Routing number failed checksum validation. Please double-check.')
      return undefined
    }

    // Race-condition guard: only the latest in-flight request wins.
    lookupSeqRef.current += 1
    const seq = lookupSeqRef.current
    setBankLookupLoading(true)

    const timer = setTimeout(async () => {
      try {
        const res = await authenticatedFetchJson(apiEndpoints.billingLookupRoutingNumber(rn))
        if (seq !== lookupSeqRef.current) return // stale response
        const data = res?.data
        if (data && data.bank_name) {
          setFields((prev) => ({ ...prev, bank_name: data.bank_name }))
          setBankResolved(true)
        } else {
          setBankResolved(false)
          setBankLookupError('Bank not found for that routing number.')
        }
      } catch (err) {
        if (seq !== lookupSeqRef.current) return
        const status = err && err.status
        if (status === 404) {
          setBankResolved(false)
          setBankLookupError('Bank not found for that routing number.')
        } else {
          setBankResolved(false)
          setBankLookupError(
            'Couldn’t look up the bank — please re-enter the routing number to confirm.',
          )
        }
      } finally {
        if (seq === lookupSeqRef.current) setBankLookupLoading(false)
      }
    }, ROUTING_LOOKUP_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [fields.routing_number])

  const handleFieldChange = (key, raw) => {
    let value = raw
    if (
      key === 'routing_number' ||
      key === 'routing_number_confirm' ||
      key === 'account_number' ||
      key === 'account_number_confirm'
    ) {
      value = String(raw).replace(/\D/g, '')
    }
    if (key === 'zip') {
      value = String(raw)
        .replace(/[^0-9-]/g, '')
        .slice(0, 10)
    }
    if (key === 'state') {
      value = String(raw).toUpperCase()
    }
    setFields((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }))

    if (key === 'routing_number') {
      // Resetting routing wipes the resolved bank + any prior confirm input —
      // the user is starting over. Also clear any stale confirm-field error so
      // a previous submit attempt's "do not match" message doesn't linger
      // against an empty confirm field.
      setBankResolved(false)
      setFields((prev) => ({ ...prev, [key]: value, bank_name: '', routing_number_confirm: '' }))
      if (errors.routing_number_confirm) {
        setErrors((prev) => ({ ...prev, routing_number_confirm: '' }))
      }
    }

    // Manual edit of any address field clears mapbox verification.
    if (['address_line1', 'address_line2', 'city', 'state', 'zip'].includes(key)) {
      setAddressVerifiedByMapbox(false)
      setMapboxPlaceId(null)
    }
  }

  const handleMapboxPick = useCallback((picked) => {
    setFields((prev) => ({
      ...prev,
      address_line1: picked.streetLine || prev.address_line1,
      city: picked.city || prev.city,
      state: picked.state || prev.state,
      zip: picked.zip || prev.zip,
    }))
    setMapboxPlaceId(picked.mapboxPlaceId || null)
    setAddressVerifiedByMapbox(Boolean(picked.verified))
  }, [])

  const validate = () => {
    const next = {}
    if (!fields.account_holder_name.trim()) next.account_holder_name = 'Required'
    if (!fields.account_type) next.account_type = 'Required'
    if (!/^\d{9}$/.test(fields.routing_number)) next.routing_number = 'Must be 9 digits'
    else if (!isValidAbaChecksum(fields.routing_number)) next.routing_number = 'Failed ABA checksum'
    if (!/^\d{4,17}$/.test(fields.account_number)) next.account_number = '4–17 digits'
    if (fields.account_number !== fields.account_number_confirm)
      next.account_number_confirm = 'Account numbers do not match'
    if (!fields.address_line1.trim()) next.address_line1 = 'Required'
    if (!fields.city.trim()) next.city = 'Required'
    if (!US_STATES.some(([code]) => code === fields.state)) next.state = 'Pick a US state'
    if (!ZIP_REGEX.test(fields.zip)) next.zip = '12345 or 12345-6789'
    if (!authorized) next.authorized = 'Authorization required'
    // Routing number must either resolve to a known bank OR be re-typed in the
    // confirm field. We never ask the user to type the bank name themselves.
    if (!bankResolved && !routingConfirmed) {
      if (
        fields.routing_number_confirm &&
        fields.routing_number_confirm !== fields.routing_number
      ) {
        next.routing_number_confirm = 'Routing numbers do not match'
      } else {
        next.routing_number_confirm = 'Re-enter the routing number to confirm'
      }
    }
    return next
  }

  const canSubmit = (() => {
    if (!authorized) return false
    if (!bankResolved && !routingConfirmed) return false
    return Object.keys(validate()).length === 0
  })()

  const handleSubmit = async (e) => {
    e.preventDefault()
    const v = validate()
    setErrors(v)
    if (Object.keys(v).length > 0) return

    setSubmitting(true)
    setSubmitError(null)
    try {
      const body = {
        payment_type: 'ach',
        account_holder_name: fields.account_holder_name.trim(),
        account_type: fields.account_type,
        routing_number: fields.routing_number,
        account_number: fields.account_number,
        // Only send bank_name when the lookup actually resolved a bank.
        // Confirmed-routing saves intentionally store null bank_name.
        bank_name: bankResolved && fields.bank_name ? fields.bank_name.trim() : null,
        address_line1: fields.address_line1.trim(),
        address_line2: fields.address_line2.trim() || null,
        city: fields.city.trim(),
        state: fields.state,
        zip: fields.zip.trim(),
        country: 'US',
        mapbox_place_id: mapboxPlaceId,
        address_verified_by_mapbox: addressVerifiedByMapbox,
      }
      await authenticatedFetchJson(apiEndpoints.billingCustomerBankDetails(billingCustomerId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      onSaved()
    } catch (err) {
      logger.error('Failed to save ACH details:', err)
      setSubmitError(err.message || 'Failed to save bank details')
    } finally {
      setSubmitting(false)
    }
  }

  // PAD is no longer handled by this form — it's collected via Stripe Elements.
  // Defensive: if a caller passes paymentType='pad', render a no-op so we don't crash.
  if (paymentType !== 'ach') {
    return (
      <div className={styles.padDeprecatedNote} data-testid="pad-handled-by-stripe">
        PAD is now collected via the Stripe form. This component handles ACH only.
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={styles.inlineForm} data-testid="ach-form">
      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="ach-account-holder">
          Account holder name
        </label>
        <input
          id="ach-account-holder"
          className={`${styles.textInput} ${errors.account_holder_name ? styles.textInputError : ''}`}
          type="text"
          maxLength={100}
          value={fields.account_holder_name}
          onChange={(e) => handleFieldChange('account_holder_name', e.target.value)}
          data-testid="field-account_holder_name"
        />
        {errors.account_holder_name && (
          <span className={styles.fieldError}>{errors.account_holder_name}</span>
        )}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="ach-account-type">
          Account type
        </label>
        <select
          id="ach-account-type"
          className={styles.selectInput}
          value={fields.account_type}
          onChange={(e) => handleFieldChange('account_type', e.target.value)}
          data-testid="field-account_type"
        >
          <option value="" disabled>
            Select…
          </option>
          <option value="checking">Checking</option>
          <option value="savings">Savings</option>
        </select>
        {errors.account_type && <span className={styles.fieldError}>{errors.account_type}</span>}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="ach-routing">
          Routing number
        </label>
        <input
          id="ach-routing"
          className={`${styles.textInput} ${errors.routing_number ? styles.textInputError : ''}`}
          type="text"
          inputMode="numeric"
          maxLength={9}
          value={fields.routing_number}
          onChange={(e) => handleFieldChange('routing_number', e.target.value)}
          data-testid="field-routing_number"
        />
        {bankLookupLoading && <span className={styles.helperText}>Looking up bank…</span>}
        {bankResolved && fields.bank_name && (
          <span className={styles.helperTextSuccess} data-testid="bank-resolved">
            Found: {fields.bank_name}
          </span>
        )}
        {errors.routing_number && (
          <span className={styles.fieldError}>{errors.routing_number}</span>
        )}
        {bankLookupError && <span className={styles.fieldError}>{bankLookupError}</span>}
      </div>

      {!bankResolved &&
        !bankLookupLoading &&
        Boolean(bankLookupError) &&
        fields.routing_number.length === 9 &&
        isValidAbaChecksum(fields.routing_number) && (
          <div className={styles.manualPanel} data-testid="routing-confirm-panel">
            <p className={styles.manualPanelTitle}>
              We couldn&rsquo;t look up your bank from this routing number. Re-enter the routing
              number below to confirm — the routing number is valid; this just protects against
              typos.
            </p>
            <label className={styles.fieldLabel} htmlFor="ach-routing-confirm">
              Re-enter routing number
            </label>
            <input
              id="ach-routing-confirm"
              className={`${styles.textInput} ${errors.routing_number_confirm ? styles.textInputError : ''}`}
              type="text"
              inputMode="numeric"
              maxLength={9}
              value={fields.routing_number_confirm}
              onChange={(e) => handleFieldChange('routing_number_confirm', e.target.value)}
              data-testid="field-routing_number_confirm"
            />
            {errors.routing_number_confirm && (
              <span className={styles.fieldError}>{errors.routing_number_confirm}</span>
            )}
            {routingConfirmed && (
              <span className={styles.helperTextSuccess} data-testid="routing-confirmed">
                Routing number confirmed.
              </span>
            )}
          </div>
        )}

      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="ach-account-number">
            Account number
          </label>
          <input
            id="ach-account-number"
            className={`${styles.textInput} ${errors.account_number ? styles.textInputError : ''}`}
            type="text"
            inputMode="numeric"
            maxLength={17}
            value={fields.account_number}
            onChange={(e) => handleFieldChange('account_number', e.target.value)}
            data-testid="field-account_number"
          />
          {errors.account_number && (
            <span className={styles.fieldError}>{errors.account_number}</span>
          )}
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="ach-account-number-confirm">
            Confirm account number
          </label>
          <input
            id="ach-account-number-confirm"
            className={`${styles.textInput} ${errors.account_number_confirm ? styles.textInputError : ''}`}
            type="text"
            inputMode="numeric"
            maxLength={17}
            value={fields.account_number_confirm}
            onChange={(e) => handleFieldChange('account_number_confirm', e.target.value)}
            data-testid="field-account_number_confirm"
          />
          {errors.account_number_confirm && (
            <span className={styles.fieldError}>{errors.account_number_confirm}</span>
          )}
        </div>
      </div>

      <div className={styles.sectionDivider}>Billing address</div>

      <MapboxAddressAutocomplete
        onPick={handleMapboxPick}
        onClear={() => {
          setAddressVerifiedByMapbox(false)
          setMapboxPlaceId(null)
        }}
        country="US"
      />

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="ach-line1">
          Address line 1
        </label>
        <input
          id="ach-line1"
          className={`${styles.textInput} ${errors.address_line1 ? styles.textInputError : ''}`}
          type="text"
          maxLength={200}
          value={fields.address_line1}
          onChange={(e) => handleFieldChange('address_line1', e.target.value)}
          data-testid="field-address_line1"
        />
        {errors.address_line1 && <span className={styles.fieldError}>{errors.address_line1}</span>}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="ach-line2">
          Address line 2 (optional)
        </label>
        <input
          id="ach-line2"
          className={styles.textInput}
          type="text"
          maxLength={200}
          value={fields.address_line2}
          onChange={(e) => handleFieldChange('address_line2', e.target.value)}
          data-testid="field-address_line2"
        />
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="ach-city">
            City
          </label>
          <input
            id="ach-city"
            className={`${styles.textInput} ${errors.city ? styles.textInputError : ''}`}
            type="text"
            value={fields.city}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            data-testid="field-city"
          />
          {errors.city && <span className={styles.fieldError}>{errors.city}</span>}
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="ach-state">
            State
          </label>
          <select
            id="ach-state"
            className={`${styles.selectInput} ${errors.state ? styles.textInputError : ''}`}
            value={fields.state}
            onChange={(e) => handleFieldChange('state', e.target.value)}
            data-testid="field-state"
          >
            <option value="">—</option>
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>
                {code} — {name}
              </option>
            ))}
          </select>
          {errors.state && <span className={styles.fieldError}>{errors.state}</span>}
        </div>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="ach-zip">
            ZIP code
          </label>
          <input
            id="ach-zip"
            className={`${styles.textInput} ${errors.zip ? styles.textInputError : ''}`}
            type="text"
            inputMode="numeric"
            value={fields.zip}
            onChange={(e) => handleFieldChange('zip', e.target.value)}
            data-testid="field-zip"
            placeholder="12345 or 12345-6789"
          />
          {errors.zip && <span className={styles.fieldError}>{errors.zip}</span>}
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="ach-country">
            Country
          </label>
          <input
            id="ach-country"
            className={styles.textInput}
            type="text"
            value="US"
            disabled
            data-testid="field-country"
          />
        </div>
      </div>

      <label className={styles.authBox}>
        <input
          type="checkbox"
          checked={authorized}
          onChange={(e) => setAuthorized(e.target.checked)}
          className={styles.authCheckbox}
          data-testid="authorization-checkbox"
        />
        <span className={styles.authText}>{ACH_AUTH}</span>
      </label>

      {submitError && <div className={styles.submitError}>{submitError}</div>}

      <button
        type="submit"
        className={styles.saveButton}
        disabled={!canSubmit || submitting}
        data-testid="submit-bank-btn"
      >
        {submitting ? 'Saving…' : 'Save payment method'}
      </button>
    </form>
  )
}

InlineBankForm.propTypes = {
  paymentType: PropTypes.oneOf(['ach', 'pad']).isRequired,
  billingCustomerId: PropTypes.number.isRequired,
  onSaved: PropTypes.func.isRequired,
  existingDetails: PropTypes.object,
}

export default InlineBankForm
